'use client';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import { cn } from '@commenda/utils';
import { cva } from 'class-variance-authority';
import { IconButton } from '../IconButton';
import { Icons } from '../Icons';
import { BS_DURATION_MS } from './constants';
import { useDrag } from './useDrag';
import { prefersReducedMotion } from './utils';

function composeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') ref(node);
      else if (ref != null)
        (ref as React.MutableRefObject<T | null>).current = node;
    });
  };
}

// The bottom variant reads open from here to self-manage its slide lifecycle.
type RootContextValue = { open: boolean; setOpen: (open: boolean) => void };
const RootContext = React.createContext<RootContextValue | null>(null);
const useRootContext = () => {
  const ctx = React.useContext(RootContext);
  if (!ctx) throw new Error('Sheet subcomponents must be used within <Sheet>');
  return ctx;
};

type SheetProps = React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root>;

const Sheet = ({
  open,
  defaultOpen,
  onOpenChange,
  children,
  ...rest
}: SheetProps) => {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const isControlled = open !== undefined;
  const currentOpen = isControlled ? open : internalOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  const ctx = React.useMemo<RootContextValue>(
    () => ({ open: !!currentOpen, setOpen }),
    [currentOpen, setOpen]
  );

  return (
    <RootContext.Provider value={ctx}>
      <SheetPrimitive.Root open={currentOpen} onOpenChange={setOpen} {...rest}>
        {children}
      </SheetPrimitive.Root>
    </RootContext.Provider>
  );
};

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-black/5 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

// Side panels only (top/left/right). `side="bottom"` is routed to the bottom-sheet branch.
const sheetVariants = cva(
  'fixed z-50 gap-4 bg-bg-primary p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
        right:
          'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
);

// Bottom sheet panel — vaul-faithful slide, driven by data-bs-visible / data-bs-dragging.
// Full-bleed 90dvh column; the consumer supplies the inner scroller for its body.
const bottomSheetPanel = cn(
  'fixed inset-x-0 bottom-0 z-50 w-full max-w-none shadow-xl outline-none transform-gpu',
  'transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]',
  'data-[bs-visible=true]:translate-y-0 data-[bs-visible=false]:translate-y-full',
  'data-[bs-dragging=true]:transition-none',
  'h-[90dvh] flex flex-col bg-bg-light rounded-t-3xl pb-[env(safe-area-inset-bottom)]'
);

type SheetContentBaseProps = React.ComponentPropsWithoutRef<
  typeof SheetPrimitive.Content
> & {
  /** Hide the top-right close (X) icon rendered by SheetHeader. */
  hideCloseIcon?: boolean;
};

/** Standard side panel (top/left/right) — Radix Presence + slide keyframes. */
interface SheetSidePanelProps extends SheetContentBaseProps {
  side?: 'top' | 'left' | 'right';
  /** When true the semi-transparent backdrop overlay is not rendered. */
  hideOverlay?: boolean;
}

/** Bottom sheet — vaul-style slide + drag-to-dismiss. */
interface SheetBottomProps extends SheetContentBaseProps {
  side: 'bottom';
  /** Drag-to-dismiss + a draggable handle. Default `true`. */
  dismissible?: boolean;
  /** Keep the sheet open on overlay/outside click (ESC still closes). */
  preventCloseOnOutsideClick?: boolean;
  /** Visually-hidden dialog title for a11y when no visible title is rendered. */
  srTitle?: string;
}

// One type per side, so a bottom-only prop on a side panel (or vice-versa) fails to compile.
type SheetContentProps = SheetSidePanelProps | SheetBottomProps;

const SheetCloseContext = React.createContext<{ hideCloseIcon: boolean }>({
  hideCloseIcon: false,
});

const SheetSidePanelContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetSidePanelProps
>(
  (
    {
      side = 'right',
      className,
      children,
      hideOverlay = false,
      hideCloseIcon = false,
      ...props
    },
    ref
  ) => (
    <SheetPortal>
      {!hideOverlay && <SheetOverlay />}
      <SheetPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        <SheetCloseContext.Provider value={{ hideCloseIcon }}>
          {children}
        </SheetCloseContext.Provider>
      </SheetPrimitive.Content>
    </SheetPortal>
  )
);
SheetSidePanelContent.displayName = 'SheetSidePanelContent';

const SheetBottomContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetBottomProps
>(
  (
    {
      className,
      children,
      dismissible = true,
      preventCloseOnOutsideClick = false,
      srTitle,
      hideCloseIcon = false,
      // `side` only picks this branch; don't pass it through to the DOM.
      side: _side,
      ...props
    },
    forwardedRef
  ) => {
    const { open, setOpen } = useRootContext();

    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const overlayRef = React.useRef<HTMLDivElement | null>(null);
    // These sheets have no trigger button, so remember what was focused and put it back on close.
    const lastFocusedRef = React.useRef<HTMLElement | null>(null);
    // Keyed off state, not a ref: a ref gives no signal when it is filled, so an effect reading
    // it can land before the node exists.
    const [panelNode, setPanelNode] = React.useState<HTMLDivElement | null>(
      null
    );
    const composedRef = React.useMemo(
      () => composeRefs<HTMLDivElement>(forwardedRef, panelRef, setPanelNode),
      [forwardedRef]
    );
    const [handleEl, setHandleEl] = React.useState<HTMLElement | null>(null);
    const handleRef = React.useCallback(
      (node: HTMLElement | null) => setHandleEl(node),
      []
    );

    // Radix Presence only waits for keyframe animations, not transitions, so we own mount/visibility.
    const [rendered, setRendered] = React.useState(open);
    const [visible, setVisible] = React.useState(false);

    const { cancelPendingRelease } = useDrag({
      enabled: dismissible,
      onDismiss: () => setOpen(false),
      panelRef,
      overlayRef,
      handleEl,
    });

    React.useEffect(() => {
      if (open) {
        setRendered(true);
        return undefined;
      }
      // If a drag is still settling when we close (e.g. ESC), stop it so its inline styles
      // don't block the close slide. Does nothing once a drag has finished.
      cancelPendingRelease();
      setVisible(false);
      const ms = prefersReducedMotion() ? 0 : BS_DURATION_MS;
      const id = setTimeout(() => setRendered(false), ms);
      return () => clearTimeout(id);
    }, [open, cancelPendingRelease]);

    // Runs when the panel attaches. Scheduled next to setRendered it raced the mount, and the
    // panel took its open position on the paint it appeared on, so it popped instead of sliding.
    React.useEffect(() => {
      if (!open || !panelNode) return undefined;

      // A drag-dismiss can leave inline styles that override the open slide; clear them.
      panelNode.style.transform = '';
      panelNode.style.transition = '';
      panelNode.removeAttribute('data-bs-dragging');
      const overlay = overlayRef.current;
      if (overlay) {
        overlay.style.opacity = '';
        overlay.style.transition = '';
      }

      if (prefersReducedMotion()) {
        setVisible(true);
        return undefined;
      }

      // Read layout so the closed position is committed before the class flips to open.
      panelNode.getBoundingClientRect();
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }, [open, panelNode]);

    // Remember what was focused when we open (unless focus is already inside the sheet) so
    // onCloseAutoFocus can put it back.
    React.useEffect(() => {
      if (!open) return;
      const active = document.activeElement as HTMLElement | null;
      // Capture-or-null every open, so a reopen can't restore to a previous open's element.
      lastFocusedRef.current =
        active &&
        active !== document.body &&
        !panelRef.current?.contains(active)
          ? active
          : null;
    }, [open]);

    if (!rendered) return null;

    return (
      <SheetPortal forceMount>
        <SheetPrimitive.Overlay forceMount asChild>
          <div
            ref={overlayRef}
            data-bs-visible={visible}
            className={cn(
              'fixed inset-0 z-50 bg-black/40',
              'transition-opacity duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]',
              'data-[bs-visible=true]:opacity-100 data-[bs-visible=false]:opacity-0'
            )}
          />
        </SheetPrimitive.Overlay>
        <SheetPrimitive.Content
          ref={composedRef}
          forceMount
          data-bs-visible={visible}
          // We don't render a description; tell Radix that's intentional so it doesn't warn.
          // A consumer can still pass its own aria-describedby via {...props}.
          aria-describedby={undefined}
          onInteractOutside={
            preventCloseOnOutsideClick ? (e) => e.preventDefault() : undefined
          }
          onCloseAutoFocus={(e) => {
            // Only restore to a still-attached opener; if it's gone, let Radix handle focus
            // so it never silently drops to <body>. Clear the ref either way.
            const opener = lastFocusedRef.current;
            lastFocusedRef.current = null;
            if (opener?.isConnected) {
              e.preventDefault();
              opener.focus();
            }
          }}
          className={cn(bottomSheetPanel, className)}
          {...props}
        >
          {srTitle ? (
            <SheetPrimitive.Title className="sr-only">
              {srTitle}
            </SheetPrimitive.Title>
          ) : null}
          {dismissible && (
            <div
              ref={handleRef}
              aria-hidden="true"
              className={cn(
                'shrink-0 h-1.5 w-[95px] rounded-lg bg-fg-quinary',
                'cursor-grab active:cursor-grabbing touch-none select-none',
                'mx-auto mt-4 mb-2'
              )}
            />
          )}
          {!hideCloseIcon && (
            <SheetPrimitive.Close asChild>
              <IconButton
                icon={<Icons.XClose className="size-5" />}
                aria-label="Close"
                variant="default"
                size="md"
                className="absolute right-4 top-4 z-10"
              />
            </SheetPrimitive.Close>
          )}
          <SheetCloseContext.Provider value={{ hideCloseIcon }}>
            {children}
          </SheetCloseContext.Provider>
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  }
);
SheetBottomContent.displayName = 'SheetBottomContent';

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>((props, ref) =>
  props.side === 'bottom' ? (
    <SheetBottomContent ref={ref} {...props} />
  ) : (
    <SheetSidePanelContent ref={ref} {...props} />
  )
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { hideCloseIcon } = React.useContext(SheetCloseContext);
  return (
    <div
      className={cn(
        'flex flex-row items-center justify-between gap-3 text-left',
        className
      )}
      {...props}
    >
      {children}
      {!hideCloseIcon && (
        <SheetPrimitive.Close
          aria-label="Close"
          className="flex size-6 items-center justify-center shrink-0 rounded-md text-fg-quaternary hover:bg-bg-secondary hover:text-fg-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fg-tertiary disabled:pointer-events-none"
        >
          <Icons.XClose className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      )}
    </div>
  );
};
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('txt-large-bold text-text-primary', className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('txt-small-regular text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
